import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Shield, Plus, Trash2, Edit2, Save, X } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { exemptionsApi, ExemptionRuleCreate, ExemptionRuleListItem } from '../services/api';

const ExemptionPage: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const { currentProject, loadProject } = useApp();
  
  const [exemptions, setExemptions] = useState<ExemptionRuleListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<ExemptionRuleCreate>({
    exemption_type: 'keyword',
    name: '',
    keywords: [],
    clause_numbers: [],
    clause_ids: [],
    text_patterns: [],
    reason: '',
  });
  
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (projectId && projectId !== 'new') {
      loadProject(projectId);
      loadExemptions();
    }
  }, [projectId]);

  const loadExemptions = async () => {
    if (!projectId || projectId === 'new') return;
    
    setLoading(true);
    try {
      const data = await exemptionsApi.list(projectId);
      setExemptions(data);
    } catch (e) {
      console.error('Failed to load exemptions:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!projectId || projectId === 'new') return;
    if (!formData.name.trim()) {
      alert('请输入豁免规则名称');
      return;
    }
    
    try {
      await exemptionsApi.create(projectId, formData);
      await loadExemptions();
      resetForm();
    } catch (e) {
      console.error('Failed to create exemption:', e);
      alert('创建失败，请重试');
    }
  };

  const handleUpdate = async () => {
    if (!projectId || projectId === 'new' || !editingId) return;
    
    try {
      await exemptionsApi.update(projectId, editingId, formData);
      await loadExemptions();
      resetForm();
    } catch (e) {
      console.error('Failed to update exemption:', e);
      alert('更新失败，请重试');
    }
  };

  const handleDelete = async (id: string) => {
    if (!projectId || projectId === 'new') return;
    if (!confirm('确定要删除此豁免规则吗？')) return;
    
    try {
      await exemptionsApi.delete(projectId, id);
      await loadExemptions();
    } catch (e) {
      console.error('Failed to delete exemption:', e);
      alert('删除失败，请重试');
    }
  };

  const handleEdit = async (exemption: ExemptionRuleListItem) => {
    if (!projectId || projectId === 'new') return;
    
    try {
      const full = await exemptionsApi.get(projectId, exemption.id);
      setEditingId(full.id);
      setFormData({
        exemption_type: full.exemption_type,
        name: full.name,
        keywords: full.keywords || [],
        clause_numbers: full.clause_numbers || [],
        clause_ids: full.clause_ids || [],
        text_patterns: full.text_patterns || [],
        reason: full.reason || '',
      });
      setShowForm(true);
    } catch (e) {
      console.error('Failed to load exemption details:', e);
      alert('加载详情失败');
    }
  };

  const resetForm = () => {
    setFormData({
      exemption_type: 'keyword',
      name: '',
      keywords: [],
      clause_numbers: [],
      clause_ids: [],
      text_patterns: [],
      reason: '',
    });
    setInputValue('');
    setShowForm(false);
    setEditingId(null);
  };

  const addItem = () => {
    if (!inputValue.trim()) return;
    
    const field = getFieldForType(formData.exemption_type);
    setFormData(prev => {
      const current = (prev[field] as string[] | undefined) || [];
      return { ...prev, [field]: [...current, inputValue.trim()] };
    });
    setInputValue('');
  };

  const removeItem = (index: number) => {
    const field = getFieldForType(formData.exemption_type);
    setFormData(prev => {
      const current = (prev[field] as string[] | undefined) || [];
      return { ...prev, [field]: current.filter((_: string, i: number) => i !== index) };
    });
  };

  const getFieldForType = (type: string): keyof ExemptionRuleCreate => {
    switch (type) {
      case 'keyword':
        return 'keywords';
      case 'clause_number':
        return 'clause_numbers';
      case 'clause_id':
        return 'clause_ids';
      case 'text_match':
        return 'text_patterns';
      default:
        return 'keywords';
    }
  };

  const getCurrentItems = (): string[] => {
    const field = getFieldForType(formData.exemption_type);
    return (formData[field] as string[] | undefined) || [];
  };

  const getTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      keyword: '关键词匹配',
      clause_number: '条款编号匹配',
      clause_id: '条款ID匹配',
      text_match: '文本内容匹配',
    };
    return labels[type] || type;
  };

  const getInputPlaceholder = (): string => {
    switch (formData.exemption_type) {
      case 'keyword':
        return '输入关键词，如：标准条款';
      case 'clause_number':
        return '输入条款编号，如：第一条';
      case 'clause_id':
        return '输入条款ID';
      case 'text_match':
        return '输入文本模式';
      default:
        return '输入内容';
    }
  };

  return (
    <div className="flex-1 bg-slate-50 p-6 overflow-y-auto">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Shield size={28} className="text-blue-600" />
              豁免条例管理
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              设置豁免规则，符合条件的条款将跳过AI审查
            </p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {showForm ? <X size={16} /> : <Plus size={16} />}
            {showForm ? '取消' : '新建规则'}
          </button>
        </div>

        {/* Create/Edit Form */}
        {showForm && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
            <h3 className="font-bold text-slate-800 mb-4">
              {editingId ? '编辑豁免规则' : '创建豁免规则'}
            </h3>
            
            <div className="space-y-4">
              {/* Rule Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  规则名称 <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="例如：标准条款豁免"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              {/* Exemption Type */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  匹配类型
                </label>
                <select
                  value={formData.exemption_type}
                  onChange={(e) => setFormData({ ...formData, exemption_type: e.target.value as ExemptionRuleCreate['exemption_type'] })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="keyword">关键词匹配</option>
                  <option value="clause_number">条款编号匹配</option>
                  <option value="clause_id">条款ID匹配</option>
                  <option value="text_match">文本内容匹配</option>
                </select>
              </div>

              {/* Dynamic Input */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  匹配条件
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addItem()}
                    placeholder={getInputPlaceholder()}
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                  <button
                    onClick={addItem}
                    className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                  >
                    添加
                  </button>
                </div>
                
                {/* Items List */}
                {getCurrentItems().length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {getCurrentItems().map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                      >
                        <span>{item}</span>
                        <button
                          onClick={() => removeItem(index)}
                          className="text-blue-500 hover:text-blue-700"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  豁免原因
                </label>
                <textarea
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="说明为什么这些条款需要豁免..."
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={resetForm}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={editingId ? handleUpdate : handleCreate}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Save size={16} />
                  {editingId ? '保存' : '创建'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Exemptions List */}
        <div className="space-y-3">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin"></div>
            </div>
          ) : exemptions.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
              <Shield size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">暂无豁免规则</p>
              <p className="text-sm text-slate-400 mt-1">点击"新建规则"创建第一个豁免规则</p>
            </div>
          ) : (
            exemptions.map((exemption) => (
              <div
                key={exemption.id}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-bold text-slate-800">{exemption.name}</h3>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                        {getTypeLabel(exemption.exemption_type)}
                      </span>
                    </div>
                    
                    {exemption.reason && (
                      <p className="text-sm text-slate-600 mb-2">{exemption.reason}</p>
                    )}
                    
                    <p className="text-xs text-slate-400 mt-2">
                      创建于 {new Date(exemption.created_at).toLocaleString('zh-CN')}
                    </p>
                  </div>
                  
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleEdit(exemption)}
                      className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="编辑"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(exemption.id)}
                      className="p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                      title="删除"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ExemptionPage;
